import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Stack,
  Chip,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Visibility,
  VisibilityOff,
  QrCode2,
  History,
  Refresh,
  ContentCopy,
  AccountBalance,
  PhotoSizeSelectActual,
  Smartphone,
  SettingsOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import type { AdminSePayBankInfo, SePayTransactionRow } from '../../services/api';
import SePayTransactionsTable from '../shared/SePayTransactionsTable';
import PhotosSegmentedControl from '../shared/PhotosSegmentedControl';

interface UserSePayProps {
  userId: string;
}

const MASK_DISPLAY = `${'•'.repeat(24)}`;
const REVEAL_MS = 5 * 60 * 1000;
const TOAST_MS = 2200;

const fmt = (n: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(n);

function digitsOnlyVndPrice(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 9);
}

const VND_GROUP_DISPLAY = new Intl.NumberFormat('vi-VN');

function formatVndPriceDisplay(digits: string): string {
  if (!digits) return '';
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n)) return '';
  return VND_GROUP_DISPLAY.format(n);
}

type AdminSePayTab = 'config' | 'price' | 'device';

const UserSePay: React.FC<UserSePayProps> = ({ userId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sePayEnabled, setSePayEnabled] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [invoicePrefix, setInvoicePrefix] = useState('');

  const [secretKey, setSecretKey] = useState('');
  const [bankInfo, setBankInfo] = useState<AdminSePayBankInfo | null>(null);
  const [revealExpiresAt, setRevealExpiresAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [keyVisible, setKeyVisible] = useState(false);

  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwDialogMode, setPwDialogMode] = useState<'reveal' | 'save' | 'savePrice'>('reveal');
  const [pwInput, setPwInput] = useState('');

  const [newTokenInput, setNewTokenInput] = useState('');
  const [newPrefixInput, setNewPrefixInput] = useState('');

  const [qrAmount, setQrAmount] = useState('');
  const [qrDesc, setQrDesc] = useState('');
  const [qrBusy, setQrBusy] = useState(false);
  const [qrDialogData, setQrDialogData] = useState<{
    qrUrl: string;
    amount: number;
    content: string;
    accountNumber: string;
    accountName: string;
    bankShortName: string;
    orderInvoiceNumber: string;
  } | null>(null);

  const [txns, setTxns] = useState<SePayTransactionRow[]>([]);
  const [txnsLoading, setTxnsLoading] = useState(false);

  const [price4x6Input, setPrice4x6Input] = useState('');
  const [price4x6Saved, setPrice4x6Saved] = useState<number | null>(null);
  const [adminSePayTab, setAdminSePayTab] = useState<AdminSePayTab>('config');
  const [sePayMobileLiteRequireCheckout, setSePayMobileLiteRequireCheckout] = useState(true);
  const [sePayMobileProRequireCheckout, setSePayMobileProRequireCheckout] = useState(true);
  const [deviceFlagsBusy, setDeviceFlagsBusy] = useState(false);

  const unlocked = revealExpiresAt != null && nowTick < revealExpiresAt;
  const secsLeft = unlocked ? Math.max(0, Math.ceil(((revealExpiresAt ?? 0) - nowTick) / 1000)) : 0;

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!revealExpiresAt) return;
    if (nowTick < revealExpiresAt) return;
    setRevealExpiresAt(null);
    setSecretKey('');
    setBankInfo(null);
    setKeyVisible(false);
    toast.info('Đã ẩn thông tin SePay của user (hết phiên 5 phút).', { autoClose: TOAST_MS });
  }, [revealExpiresAt, nowTick]);

  const loadMeta = useCallback(async () => {
    try {
      setLoading(true);
      const d = await api.getAdminUserSePaySettings(userId);
      setConfigured(d.configured);
      setSePayEnabled(d.sePayEnabled === true);
      setInvoicePrefix(d.invoicePrefix || '');
      const p46 = d.price4x6Vnd;
      setPrice4x6Saved(p46 ?? null);
      setPrice4x6Input(p46 != null ? formatVndPriceDisplay(String(p46)) : '');
      setSePayMobileLiteRequireCheckout(d.sePayMobileLiteRequireCheckout ?? true);
      setSePayMobileProRequireCheckout(d.sePayMobileProRequireCheckout ?? true);
      setSecretKey('');
      setBankInfo(null);
      setRevealExpiresAt(null);
      setKeyVisible(false);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Không tải được cấu hình SePay.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  const loadTxns = useCallback(async () => {
    setTxnsLoading(true);
    try {
      const cleanup = await api.cleanupAdminUserStaleSePayTransactions(userId);
      if (cleanup.deletedCount > 0) {
        toast.info(`Đã xóa ${cleanup.deletedCount} đơn chờ thanh toán quá 1 ngày.`, { autoClose: 4000 });
      }
      const data = await api.getAdminUserSePayTransactions(userId, 200);
      setTxns(data);
    } catch {
      toast.error('Không lấy được giao dịch SePay.');
    } finally {
      setTxnsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadTxns();
  }, [loadTxns]);

  const handleToggleEnabled = async () => {
    if (busy) return;
    const next = !sePayEnabled;
    try {
      setBusy(true);
      await api.toggleAdminUserSePayEnabled(userId, next);
      setSePayEnabled(next);
      toast.success(next ? 'Đã bật SePay cho user.' : 'Đã tắt SePay cho user.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Không cập nhật được trạng thái.');
    } finally {
      setBusy(false);
    }
  };

  const saveAdminMobileCheckoutFlags = async (patch: {
    sePayMobileLiteRequireCheckout?: boolean;
    sePayMobileProRequireCheckout?: boolean;
  }) => {
    if (deviceFlagsBusy) return;
    try {
      setDeviceFlagsBusy(true);
      const r = await api.putAdminUserSePayMobileCheckoutFlags(userId, patch);
      setSePayMobileLiteRequireCheckout(r.sePayMobileLiteRequireCheckout);
      setSePayMobileProRequireCheckout(r.sePayMobileProRequireCheckout);
      toast.success(r.message || 'Đã cập nhật.', { autoClose: TOAST_MS });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Không lưu được cờ thiết bị.');
    } finally {
      setDeviceFlagsBusy(false);
    }
  };

  const openPwDialog = (mode: 'reveal' | 'save' | 'savePrice') => {
    setPwDialogMode(mode);
    setPwInput('');
    setPwDialogOpen(true);
  };

  const confirmPw = async () => {
    if (!pwInput.trim()) {
      toast.error('Nhập mật khẩu admin.');
      return;
    }
    try {
      setBusy(true);
      if (pwDialogMode === 'reveal') {
        const d = await api.revealAdminUserSePaySettings(userId, pwInput);
        setSecretKey(d.secretKey || '');
        setBankInfo(d.bankInfo);
        setInvoicePrefix(d.invoicePrefix || '');
        setRevealExpiresAt(Date.now() + REVEAL_MS);
        setKeyVisible(true);
        setPwDialogOpen(false);
        toast.success('Mở phiên 5 phút — đang hiển thị thông tin SePay.', { autoClose: TOAST_MS });
      } else if (pwDialogMode === 'savePrice') {
        const digits = digitsOnlyVndPrice(price4x6Input);
        let value: number | null;
        if (!digits) {
          value = null;
        } else {
          const n = parseInt(digits, 10);
          if (!Number.isFinite(n) || n < 0 || n > 999_999_999) {
            toast.error('Giá không hợp lệ (0 – 999.999.999 VND).');
            setBusy(false);
            return;
          }
          value = n;
        }
        const result = await api.putAdminUserSePayPrice4x6(userId, pwInput, value);
        setPrice4x6Saved(result.price4x6Vnd);
        setPrice4x6Input(result.price4x6Vnd != null ? formatVndPriceDisplay(String(result.price4x6Vnd)) : '');
        toast.success(result.message, { autoClose: TOAST_MS });
        setPwDialogOpen(false);
        setPwInput('');
      } else {
        const key = newTokenInput.trim();
        const pfx = newPrefixInput.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
        if (!key && !pfx) {
          toast.error('Nhập API Token hoặc Prefix mã đơn.');
          setBusy(false);
          return;
        }
        const payload: { secretKey?: string; invoicePrefix?: string } = {};
        if (key) payload.secretKey = key;
        payload.invoicePrefix = pfx;
        const result = await api.putAdminUserSePaySettings(userId, pwInput, payload);
        setInvoicePrefix(result.invoicePrefix || '');
        toast.success('Đã lưu cấu hình SePay cho user.', { autoClose: TOAST_MS });
        setPwDialogOpen(false);
        setNewTokenInput('');
        setNewPrefixInput('');
        await loadMeta();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Mật khẩu không đúng hoặc thao tác thất bại.');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateQr = async () => {
    const amt = Number(qrAmount);
    if (!amt || amt < 1000) {
      toast.error('Số tiền tối thiểu 1.000 VND.');
      return;
    }
    try {
      setQrBusy(true);
      const d = await api.adminCreateSePayCheckout(userId, {
        amount: amt,
        description: qrDesc.trim() || `Thanh toán ${amt}`,
      });
      setQrDialogData({
        qrUrl: d.checkoutUrl,
        amount: d.amount,
        content: d.content,
        accountNumber: d.accountNumber,
        accountName: d.accountName,
        bankShortName: d.bankShortName,
        orderInvoiceNumber: d.orderInvoiceNumber,
      });
      void loadTxns();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Tạo QR thất bại.');
    } finally {
      setQrBusy(false);
    }
  };

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`Đã copy ${label}.`, { autoClose: 1200 }));
  };

  const sectionCard = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
  } as const;

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={180}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Box className="user-page-enter" sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.25 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.18),
          background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, #fff 55%)`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: { xs: 42, sm: 48 },
              height: { xs: 42, sm: 48 },
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.28)}`,
            }}
          >
            <AccountBalanceWallet sx={{ fontSize: { xs: 22, sm: 26 } }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              fontWeight={800}
              sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, letterSpacing: '-0.02em', lineHeight: 1.25 }}
            >
              SePay
            </Typography>
            {!isMobile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Cấu hình cổng thanh toán, giá in và giao dịch của user.
              </Typography>
            )}
          </Box>
          <Chip
            size="small"
            label={sePayEnabled ? 'BẬT' : 'TẮT'}
            color={sePayEnabled ? 'success' : 'default'}
            sx={{ fontWeight: 800, flexShrink: 0 }}
          />
        </Stack>

        <Box
          sx={{
            mt: 1.5,
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between', ml: 0 }}
            labelPlacement="start"
            control={
              <Switch checked={sePayEnabled} onChange={handleToggleEnabled} disabled={busy} color="primary" />
            }
            label={
              <Box sx={{ pr: 1 }}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' } }}>
                  {sePayEnabled ? 'SePay đang bật' : 'SePay đang tắt'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                  Tắt sẽ khóa thanh toán SePay của user
                </Typography>
              </Box>
            }
          />
        </Box>

        {!sePayEnabled && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 1.25, borderRadius: 2, py: 0.5 }}>
            Bật SePay để user thanh toán được.
          </Alert>
        )}
      </Paper>

      {/* Segment tabs */}
      <PhotosSegmentedControl
        value={adminSePayTab}
        onChange={setAdminSePayTab}
        fullWidth
        size="sm"
        options={[
          { value: 'config', label: isMobile ? 'Cấu hình' : 'Cấu hình', icon: <SettingsOutlined fontSize="inherit" /> },
          { value: 'price', label: isMobile ? 'Giá 4×6' : 'Giá in 4×6', icon: <PhotoSizeSelectActual fontSize="inherit" /> },
          { value: 'device', label: 'Thiết bị', icon: <Smartphone fontSize="inherit" /> },
        ]}
      />

      {/* Config */}
      {adminSePayTab === 'config' && (
        <Paper elevation={0} sx={sectionCard}>
          {unlocked && (
            <Alert severity="success" variant="outlined" sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }} icon={false}>
              <Typography variant="body2">
                Phiên mở — còn <strong>{secsLeft}s</strong>
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              p: 1.5,
              mb: 1.5,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountBalance sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={800}>
                  Ngân hàng
                </Typography>
              </Stack>
              {!unlocked && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openPwDialog('reveal')}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, px: 1.5 }}
                >
                  Xem
                </Button>
              )}
            </Stack>

            {unlocked && bankInfo ? (
              <Stack spacing={0.85}>
                {[
                  { k: 'Ngân hàng', v: bankInfo.bankShortName || '—' },
                  { k: 'Số TK', v: bankInfo.accountNumber || '—', copy: bankInfo.accountNumber },
                  { k: 'Chủ TK', v: bankInfo.accountName || '—' },
                ].map((row) => (
                  <Stack key={row.k} direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 72, flexShrink: 0 }}>
                      {row.k}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: row.k === 'Số TK' ? 'ui-monospace, monospace' : 'inherit',
                        wordBreak: 'break-all',
                      }}
                    >
                      {row.v}
                    </Typography>
                    {row.copy ? (
                      <IconButton size="small" onClick={() => copyText(row.copy!, 'số TK')}>
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            ) : unlocked && !bankInfo ? (
              <Typography variant="body2" color="text.secondary">
                Chưa cấu hình tài khoản ngân hàng.
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: 'ui-monospace, monospace', letterSpacing: 0.02 }}
              >
                {configured ? MASK_DISPLAY : 'Chưa cấu hình'}
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            size="small"
            label="API Token"
            value={unlocked && keyVisible ? secretKey : configured ? MASK_DISPLAY : ''}
            placeholder={configured ? undefined : 'Chưa cấu hình'}
            autoComplete="off"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {unlocked && configured ? (
                    <IconButton size="small" onClick={() => setKeyVisible((v) => !v)} tabIndex={-1}>
                      {keyVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  ) : (
                    <span>
                      <IconButton
                        size="small"
                        disabled={!configured}
                        onClick={() => openPwDialog('reveal')}
                        tabIndex={-1}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </span>
                  )}
                  {unlocked && secretKey && (
                    <IconButton size="small" onClick={() => copyText(secretKey, 'API Token')} tabIndex={-1}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.16),
              background: `linear-gradient(160deg, #fff 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: '0.04em', display: 'block', mb: 1.25 }}
            >
              CẬP NHẬT
            </Typography>
            <Stack spacing={1.25}>
              <TextField
                size="small"
                fullWidth
                label="API Token mới"
                placeholder="Token mới (tuỳ chọn)…"
                value={newTokenInput}
                onChange={(e) => setNewTokenInput(e.target.value)}
                autoComplete="off"
              />
              <TextField
                size="small"
                fullWidth
                label="Prefix mã đơn"
                placeholder={invoicePrefix || 'MPRO'}
                value={newPrefixInput}
                onChange={(e) =>
                  setNewPrefixInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))
                }
                autoComplete="off"
                helperText={`Hiện tại: ${invoicePrefix || 'MPRO'} · VD: ${newPrefixInput || invoicePrefix || 'MPRO'}A1B2C3D4`}
                inputProps={{ maxLength: 8 }}
              />
              <Button
                fullWidth={isMobile}
                variant="contained"
                onClick={() => openPwDialog('save')}
                disabled={(!newTokenInput.trim() && !newPrefixInput.trim()) || busy}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 999,
                  px: 2.5,
                  boxShadow: 'none',
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                Lưu cấu hình
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Price */}
      {adminSePayTab === 'price' && (
        <Paper elevation={0} sx={sectionCard}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.75 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <PhotoSizeSelectActual sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                Giá in 4×6
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Để trống + Lưu để xóa giá
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box
              sx={{
                width: { xs: 96, sm: 110 },
                aspectRatio: '2 / 3',
                mx: { xs: 'auto', sm: 0 },
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'primary.main',
                background: 'linear-gradient(160deg, #f8fafc 0%, #e2e8f0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography fontWeight={800} color="primary.dark">
                4×6
              </Typography>
            </Box>
            <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                size="small"
                fullWidth
                label="Giá 1 tấm (VND)"
                value={price4x6Input}
                onChange={(e) => {
                  const d = digitsOnlyVndPrice(e.target.value);
                  setPrice4x6Input(d === '' ? '' : formatVndPriceDisplay(d));
                }}
                placeholder="VD: 20.000"
                inputProps={{ inputMode: 'numeric' }}
                helperText={
                  price4x6Saved != null ? `Đang lưu: ${fmt(price4x6Saved)}` : 'Chưa lưu giá'
                }
              />
              <Button
                fullWidth={isMobile}
                variant="contained"
                onClick={() => openPwDialog('savePrice')}
                disabled={busy}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 999,
                  boxShadow: 'none',
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                Lưu giá
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Device */}
      {adminSePayTab === 'device' && (
        <Paper elevation={0} sx={sectionCard}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <Smartphone sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                Thiết bị
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bắt buộc QR SePay trên mobile
              </Typography>
            </Box>
          </Stack>

          {!sePayEnabled && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}>
              SePay đang tắt — cờ vẫn lưu được.
            </Alert>
          )}

          <Stack spacing={1.25}>
            {[
              {
                checked: sePayMobileLiteRequireCheckout,
                onChange: (c: boolean) =>
                  void saveAdminMobileCheckoutFlags({ sePayMobileLiteRequireCheckout: c }),
                title: 'Lite — bắt buộc QR',
                sub: 'Device Management gắn máy',
              },
              {
                checked: sePayMobileProRequireCheckout,
                onChange: (c: boolean) =>
                  void saveAdminMobileCheckoutFlags({ sePayMobileProRequireCheckout: c }),
                title: 'Pro — bắt buộc QR',
                sub: 'Phiên không gắn máy',
              },
            ].map((item) => (
              <Box
                key={item.title}
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: '100%', justifyContent: 'space-between', ml: 0 }}
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={item.checked}
                      onChange={(_, c) => item.onChange(c)}
                      disabled={deviceFlagsBusy}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ pr: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} sx={{ fontSize: '0.88rem' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.sub}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* QR */}
      <Paper elevation={0} sx={sectionCard}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              flexShrink: 0,
            }}
          >
            <QrCode2 sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
              Tạo QR thanh toán
            </Typography>
            {!isMobile && (
              <Typography variant="caption" color="text.secondary">
                Tạo QR thử cho user
              </Typography>
            )}
          </Box>
        </Stack>

        {!sePayEnabled && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.25, borderRadius: 2, py: 0.5 }}>
            Bật SePay để tạo QR.
          </Alert>
        )}

        <Stack spacing={1.25}>
          <TextField
            size="small"
            fullWidth
            label="Số tiền (VND)"
            value={qrAmount}
            onChange={(e) => setQrAmount(e.target.value.replace(/\D/g, ''))}
            placeholder="50000"
            disabled={!sePayEnabled}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            size="small"
            fullWidth
            label="Nội dung chuyển khoản"
            value={qrDesc}
            onChange={(e) => setQrDesc(e.target.value)}
            placeholder="Thanh toán dịch vụ…"
            disabled={!sePayEnabled}
          />
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleCreateQr}
            disabled={!sePayEnabled || qrBusy || !qrAmount}
            startIcon={qrBusy ? <CircularProgress size={18} color="inherit" /> : <QrCode2 />}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none', py: 1.1 }}
          >
            {qrBusy ? 'Đang tạo…' : 'Tạo QR'}
          </Button>
        </Stack>
      </Paper>

      {/* Transactions */}
      <Paper elevation={0} sx={sectionCard}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <History sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                Giao dịch
              </Typography>
              <Typography variant="caption" color="text.secondary">
                20 / trang · tối đa 200
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={() => void loadTxns()}
            disabled={txnsLoading}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              flexShrink: 0,
            }}
            aria-label="Làm mới"
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Stack>

        <SePayTransactionsTable rows={txns} loading={txnsLoading} variant="admin" maxHeight={420} />
      </Paper>

      {/* Password dialog */}
      <Dialog
        open={pwDialogOpen}
        onClose={() => !busy && setPwDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.15rem' } }}>
          {pwDialogMode === 'reveal'
            ? 'Xác nhận — xem thông tin'
            : pwDialogMode === 'savePrice'
              ? 'Xác nhận — lưu giá 4×6'
              : 'Xác nhận — lưu cấu hình'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pwDialogMode === 'reveal'
              ? 'Nhập mật khẩu admin để mở phiên xem 5 phút.'
              : pwDialogMode === 'savePrice'
                ? 'Nhập mật khẩu admin để lưu giá 4×6.'
                : 'Nhập mật khẩu admin để lưu API Token / prefix.'}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Mật khẩu admin"
            type="password"
            fullWidth
            size="small"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), void confirmPw())}
            disabled={busy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setPwDialogOpen(false)}
            disabled={busy}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmPw()}
            disabled={busy}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none', minWidth: 96 }}
          >
            {busy ? (
              <CircularProgress size={16} />
            ) : pwDialogMode === 'reveal' ? (
              'Xem'
            ) : pwDialogMode === 'savePrice' ? (
              'Lưu giá'
            ) : (
              'Lưu'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR dialog */}
      <Dialog
        open={!!qrDialogData}
        onClose={() => setQrDialogData(null)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Mã QR SePay</DialogTitle>
        <DialogContent>
          {qrDialogData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <img
                src={qrDialogData.qrUrl}
                alt="QR thanh toán"
                style={{ width: isMobile ? 240 : 220, height: isMobile ? 240 : 220, objectFit: 'contain', borderRadius: 8 }}
                onError={(e) => ((e.target as HTMLImageElement).alt = 'Không tải được QR')}
              />
              <Box
                sx={{
                  width: '100%',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">
                  <strong>Ngân hàng:</strong> {qrDialogData.bankShortName}
                </Typography>
                <Typography variant="body2">
                  <strong>Số TK:</strong> {qrDialogData.accountNumber}
                </Typography>
                <Typography variant="body2">
                  <strong>Chủ TK:</strong> {qrDialogData.accountName}
                </Typography>
                <Typography variant="body2">
                  <strong>Số tiền:</strong> {fmt(qrDialogData.amount)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <strong>Nội dung:</strong> {qrDialogData.content}
                  </Typography>
                  <Tooltip title="Copy nội dung CK">
                    <IconButton size="small" onClick={() => copyText(qrDialogData.content, 'nội dung CK')}>
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Mã đơn: {qrDialogData.orderInvoiceNumber}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={() => setQrDialogData(null)}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
          >
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserSePay;
